import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, Check, ArrowLeft, AlertCircle, FileText, Calendar,
  PackageCheck, Pill, Sparkles, Loader2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const SHIPPING_FEE = 49;

/* Load Razorpay checkout JS on demand */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { user } = useAuth();
  const { items, setPrescription, subtotal, clear } = useCart();
  const nav = useNavigate();

  const [prescriptions, setPrescriptions] = useState([]);
  const [loadingRx, setLoadingRx] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [ship, setShip] = useState({
    full_name: user?.name || "",
    phone: user?.phone || user?.mobile || "",
    line1: "", line2: "", city: "", state: "", pincode: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // role gate
  useEffect(() => {
    if (user === undefined) return;            // auth still loading
    if (!user) { nav("/patient?next=" + encodeURIComponent("/b2b/checkout"), { replace: true }); return; }
    if (!["patient", "doctor"].includes(user.role)) {
      toast("Only patients and doctors can place orders.");
      nav("/");
    }
  }, [user, nav]);

  // cart gate — only redirect if cart is empty AND we're not in the middle of placing/just placed
  useEffect(() => {
    if (items.length === 0 && !busy && !placed) nav("/pharmaceuticals", { replace: true });
  }, [items.length, busy, placed, nav]);

  // load valid prescriptions
  useEffect(() => {
    setLoadingRx(true);
    api.get("/me/prescriptions/valid")
      .then((r) => setPrescriptions(r.data.prescriptions || []))
      .catch(() => setPrescriptions([]))
      .finally(() => setLoadingRx(false));
  }, []);

  const total = subtotal + SHIPPING_FEE;
  const rxItems = items.filter((it) => it.rx_required);
  const allRxAttached = rxItems.every((it) => !!it.prescription_id);

  // for each Rx item, decide if a valid Rx is *available*
  const hasAnyValidRx = prescriptions.length > 0;
  const rxBlocked = rxItems.length > 0 && !hasAnyValidRx;

  const setField = (k, v) => setShip((s) => ({ ...s, [k]: v }));

  const placeOrder = async () => {
    setErr("");
    if (!ship.full_name || !ship.phone || !ship.line1 || !ship.city || !ship.state || !ship.pincode) {
      setErr("Please fill in all shipping fields."); return;
    }
    if (!allRxAttached) {
      setErr("Please attach a valid prescription for every Rx item."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/b2b/checkout", {
        items: items.map((it) => ({
          product_id: it.product_id, qty: it.qty,
          prescription_id: it.rx_required ? it.prescription_id : "",
        })),
        shipping: ship,
      });
      const oid = data.order_id;
      const rp = data.razorpay;

      if (rp.demo || !rp.key_id) {
        // Dev mode: no real Razorpay — verify immediately
        const v = await api.post(`/b2b/orders/${oid}/verify`, {
          razorpay_payment_id: `pay_demo_${oid.slice(-8)}`,
          razorpay_signature: "",
        });
        if (v.data.status === "paid") {
          setPlaced(true);
          clear();
          nav(`/b2b/order/${oid}?demo=1`, { replace: true });
        }
      } else {
        const ok = await loadRazorpay();
        if (!ok) { setErr("Could not load Razorpay. Try again."); setBusy(false); return; }
        const rz = new window.Razorpay({
          key: rp.key_id,
          amount: rp.amount,
          currency: rp.currency,
          name: "2nd Opinion Health",
          description: `B2B order ${oid.slice(-6)}`,
          order_id: rp.order_id,
          prefill: { name: ship.full_name, contact: ship.phone, email: user.email },
          theme: { color: "#185FA5" },
          handler: async (resp) => {
            try {
              const v = await api.post(`/b2b/orders/${oid}/verify`, {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              if (v.data.status === "paid") { setPlaced(true); clear(); nav(`/b2b/order/${oid}`, { replace: true }); }
            } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Verification failed."); setBusy(false); }
          },
          modal: { ondismiss: () => setBusy(false) },
        });
        rz.open();
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not place order.");
      setBusy(false);
    }
  };

  if (!user || (items.length === 0 && !placed)) return null;

  return (
    <div className="ck-wrap" data-testid="b2b-checkout">
      <button className="ck-back" onClick={() => nav(-1)}><ArrowLeft size={15} /> Back</button>
      <h1 className="ck-h1">Checkout</h1>
      <p className="ck-sub"><ShieldCheck size={14} /> Encrypted checkout · Compliant with DPDP Act 2023</p>

      <div className="ck-grid">
        {/* LEFT: forms */}
        <div className="ck-left">
          <section className="ck-panel" data-testid="ck-ship">
            <h2>Shipping address</h2>
            <div className="ck-grid2">
              <label>Full name *<input value={ship.full_name} onChange={(e) => setField("full_name", e.target.value)} data-testid="ck-name" /></label>
              <label>Phone *<input value={ship.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="ck-phone" /></label>
              <label className="full">Address line 1 *<input value={ship.line1} onChange={(e) => setField("line1", e.target.value)} placeholder="Street + house no." data-testid="ck-line1" /></label>
              <label className="full">Line 2<input value={ship.line2} onChange={(e) => setField("line2", e.target.value)} placeholder="Apartment, landmark…" /></label>
              <label>City *<input value={ship.city} onChange={(e) => setField("city", e.target.value)} data-testid="ck-city" /></label>
              <label>State *<input value={ship.state} onChange={(e) => setField("state", e.target.value)} data-testid="ck-state" /></label>
              <label>PIN code *<input value={ship.pincode} onChange={(e) => setField("pincode", e.target.value)} data-testid="ck-pin" /></label>
            </div>
          </section>

          {rxItems.length > 0 && (
            <section className="ck-panel" data-testid="ck-rx-panel">
              <h2><FileText size={17} /> Prescriptions required</h2>
              <p className="ck-sub" style={{ margin: "0 0 14px" }}>
                Pharmacy items need a prescription issued by one of our specialists in the last 30 days.
                You can only choose from your existing in-platform prescriptions.
              </p>

              {loadingRx ? (
                <p className="ck-muted"><Loader2 size={14} className="spin" /> Loading prescriptions…</p>
              ) : !hasAnyValidRx ? (
                <div className="ck-rx-empty" data-testid="ck-rx-empty">
                  <AlertCircle size={20} />
                  <div>
                    <b>No valid prescription found.</b>
                    <p>You don&apos;t have any prescription from the last 30 days, or your last consult was more than 30 days ago. Please book a new consultation to order this medicine.</p>
                    <button className="pg-btn pg-btn-ink" onClick={() => nav("/book")} data-testid="ck-book-consult">Book a consultation</button>
                  </div>
                </div>
              ) : (
                <div className="ck-rx-list">
                  {rxItems.map((it) => (
                    <div key={it.product_id} className="ck-rx-row" data-testid={`ck-rx-${it.product_id}`}>
                      <span className="ck-rx-prod"><Pill size={14} /> {it.name}</span>
                      <select
                        value={it.prescription_id}
                        onChange={(e) => setPrescription(it.product_id, e.target.value)}
                        data-testid={`ck-rx-select-${it.product_id}`}
                      >
                        <option value="">— Select prescription —</option>
                        {prescriptions.map((rx) => (
                          <option key={rx.id} value={rx.id}>
                            {(rx.issued_at || "").slice(0, 10)} · {rx.doctor_name} · {rx.medicine_count} item(s)
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {err && <p className="ck-err" data-testid="ck-error"><AlertCircle size={14} /> {err}</p>}

          <button
            className="pg-btn pg-btn-ink full"
            onClick={placeOrder}
            disabled={busy || rxBlocked || !allRxAttached}
            data-testid="ck-place-order"
          >
            {busy ? "Processing…" : `Pay ${fmt(total)} & place order`}
          </button>
        </div>

        {/* RIGHT: summary */}
        <aside className="ck-summary" data-testid="ck-summary">
          <h2>Order summary</h2>
          <ul className="ck-items">
            {items.map((it) => {
              const Ic = it.kind === "pharmaceutical" ? Pill : Sparkles;
              return (
                <li key={it.product_id}>
                  <div className="ck-thumb">{it.image_url ? <img src={it.image_url} alt="" /> : <Ic size={18} />}</div>
                  <div className="ck-i-meta">
                    <b>{it.name}</b>
                    <small>Qty {it.qty}{it.rx_required && " · Rx"}</small>
                  </div>
                  <span className="ck-i-price">{fmt(it.price * it.qty)}</span>
                </li>
              );
            })}
          </ul>
          <div className="ck-sum-rows">
            <div><span>Subtotal</span><b>{fmt(subtotal)}</b></div>
            <div><span>Shipping</span><b>{fmt(SHIPPING_FEE)}</b></div>
            <div className="total"><span>Total</span><b>{fmt(total)}</b></div>
          </div>
          <p className="ck-muted small"><ShieldCheck size={11} /> Payments are processed by Razorpay. Your card or UPI details never touch our servers.</p>
        </aside>
      </div>
    </div>
  );
}
